import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';

const StatsCards = ({ stats = [], loading = false }) => {
  if (loading) {
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{
              borderRadius: 1,
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              bgcolor: '#f8fafc',
              height: 140
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <Typography color="#737373">Loading...</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ mb: 4 }}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={6} lg={3} xl={3} key={stat.id || index}>
          <Card sx={{
            borderRadius: 1,
            border: '0 solid #fff',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            transition: 'all 0.2s ease-in-out',
            height: '100%',
            padding: '0.5rem',
            bgcolor: '#ffffff',
            '&:hover': {
              boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
            }
          }}>
            <CardContent sx={{
              p: { xs: 3, sm: 4 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              {/* Icon and Title Row */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2
              }}>
                {(() => {
                  const isTrend = !!stat.isTrend;
                  const trendVal = isTrend ? Number(stat.value) : 0;
                  const hasNoPrev = isTrend && typeof stat.subtitle === 'string' && stat.subtitle.toLowerCase().includes('no previous data');
                  const baseCurrentZero = (() => {
                    if (stat.id === 'cycle_time_trend') {
                      const base = (stats || []).find((s) => s.id === 'avg_cycle_time');
                      const n = Number(base?.value);
                      return Number.isFinite(n) && n === 0;
                    }
                    return false;
                  })();
                  const showTrendDecor = isTrend && !hasNoPrev && !baseCurrentZero;
                  // New rule: non-negative -> up arrow + red; negative -> down arrow + green
                  const isNonNegative = isTrend && trendVal >= 0;
                  const trendColor = showTrendDecor ? (isNonNegative ? '#ef4444' : '#10b981') : '#64748b';
                  return (
                    <Box sx={{
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      color: showTrendDecor ? trendColor : '#64748b',
                      // Rotate up when non-negative; otherwise keep pointing down
                      transform: showTrendDecor && isNonNegative ? 'rotate(180deg)' : 'none',
                      '& svg': {
                        fontSize: '1.2rem'
                      }
                    }}>
                      {stat.icon}
                    </Box>
                  );
                })()}
                <Typography
                  variant="h6"
                  sx={{
                    color: '#737373',
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    fontWeight: 500,
                  }}
                >
                  {stat.title}
                </Typography>
              </Box>

              {/* Value */}
              {(() => {
                const isTrend = !!stat.isTrend;
                const trendVal = isTrend ? Number(stat.value) : 0;
                const isDecreasing = isTrend && trendVal < 0;
                const isIncreasing = isTrend && trendVal > 0;
                const hasNoPrev = isTrend && typeof stat.subtitle === 'string' && stat.subtitle.toLowerCase().includes('no previous data');
                const baseCurrentZero = (() => {
                  if (stat.id === 'cycle_time_trend') {
                    const base = (stats || []).find((s) => s.id === 'avg_cycle_time');
                    const n = Number(base?.value);
                    return Number.isFinite(n) && n === 0;
                  }
                  return false;
                })();
                const showTrendDecor = isTrend && !hasNoPrev && !baseCurrentZero;
                // New rule: non-negative -> up/red (bad), negative -> down/green (good)
                const isNonNegative = isTrend && trendVal >= 0;
                const trendColor = showTrendDecor ? (isNonNegative ? '#ef4444' : '#10b981') : '#1a202c';

                // Display absolute value for trend metrics to avoid leading minus sign
                let displayValue = stat.value;
                if (isTrend) {
                  if (!showTrendDecor) {
                    displayValue = '—';
                  } else {
                    const n = Number(stat.value);
                    displayValue = Number.isFinite(n)
                      ? Math.abs(n).toFixed(1)
                      : String(stat.value).replace('-', '');
                  }
                }

                return (
                  <Typography
                    variant="h4"
                    sx={{
                      color: trendColor,
                      mb: 1.5,
                      ml: 0.5,
                      fontSize: { xs: '1.3rem', sm: '1.675rem' },
                      fontWeight: 600,
                      lineHeight: 1.2,
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {displayValue}{stat.unit || ''}
                  </Typography>
                );
              })()}

              {/* Subtitle */}
              <Typography
                variant="body2"
                sx={{
                  color: '#737373',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  ml: 0.5,
                }}
              >
                {stat.subtitle}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards;